import { BedDouble, ExternalLink, Mountain, Star, Tent } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import { placeLinks } from "../utils/placeLink";
import type { UiLang, Lodging } from "../lib/types";
import { PlaceAltLink } from "./PlaceAltLink";

interface Props {
  lodging: Lodging[];
  lang: UiLang;
  /** Stoppet stedene ligger ved. Brukes bare i søkelenka. */
  near?: string;
}

/**
 * Hvor man kan sove nær et stopp.
 *
 * Egen liste framfor å blandes inn blant ladestasjoner og spisesteder, fordi
 * overnatting er et annet slags valg: det er én avgjørelse per natt, den må
 * tas på forhånd, og den styrer hvor dagen slutter.
 *
 * DNT-hyttene er merket. De har egne regler — medlemsnøkkel, selvbetjening,
 * ingen resepsjon — og en som tror det er et hotell får en overraskelse.
 */

const ICONS: Record<Lodging["kind"], LucideIcon> = {
  hotel: BedDouble,
  hostel: BedDouble,
  camping: Tent,
  cabin: Mountain,
};

/** Rekkefølgen listen vises i. Hytter først i fjellet er ikke tilfeldig. */
const ORDER: Lodging["kind"][] = ["hotel", "cabin", "hostel", "camping"];

export function LodgingList({ lodging, lang, near }: Props) {
  const t = dictionaries[lang];

  if (lodging.length === 0) {
    return <p className="amenity-empty">{t.noLodging}</p>;
  }

  const groups = ORDER.map((kind) => ({
    kind,
    items: lodging.filter((entry) => entry.kind === kind),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="amenity-groups">
      {groups.map(({ kind, items }) => {
        const Icon = ICONS[kind];
        return (
          <div className="amenity-group" key={kind}>
            <div className="amenity-heading">
              <Icon size={13} />
              {t.lodgingKind[kind]}
            </div>

            <ul className="amenity-items">
              {items.map((entry) => {
                const links = placeLinks(entry, near);
                return (
                  <li className="amenity-item" key={entry.id}>
                    <span className="amenity-detail">
                      <b>
                        {/*
                          Nettsiden er det som gjør oppføringen bookbar — men
                          omtrent halvparten av hotellene har ingen. De sto som
                          ren tekst før, og et hotellnavn uten en vei videre er
                          ikke til stor hjelp. Nå faller de til et kartsøk, som
                          gir både adresse, bilder og telefonnummer.
                        */}
                        <a
                          href={links.primary.href}
                          target="_blank"
                          rel="noreferrer"
                          title={`${entry.name} — ${t.lodgingKind[entry.kind]}`}
                        >
                          {entry.name} <ExternalLink size={10} />
                        </a>
                      </b>

                      {/*
                        Og den andre veien, når nettsida er den som svikter.
                        Det er her det merkes mest: et lite hotell med et
                        utløpt domene ser bookbart ut helt til man klikker.
                        Se PlaceAltLink.
                      */}
                      <PlaceAltLink link={links.fallback} lang={lang} />

                      <span className="amenity-chips">
                        {entry.dnt && <span className="amenity-fast">{t.dntHut}</span>}
                        {entry.stars && (
                          <span className="lodging-stars">
                            <Star size={9} fill="currentColor" /> {entry.stars}
                          </span>
                        )}
                      </span>
                    </span>

                    <span className="amenity-distance">{entry.distanceKm} km</span>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
